import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/chords/Button';
import { IconButton } from '~/chords/IconButton';
import { Tooltip } from '~/components';
import { env } from '~/lib/env';

const FEEDBACK_TOOLTIP_STORAGE_KEY = 'globalHeaderFeedbackTooltip';
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

function getLastOpened(stored: string | null): number | null {
  if (!stored) {
    return null;
  }

  try {
    const parsed = JSON.parse(stored);

    return typeof parsed.lastOpened === 'number' ? parsed.lastOpened : null;
  } catch {
    return null;
  }
}

interface FeedbackButtonProps {
  hideTooltip?: boolean;
}

export function FeedbackButton({ hideTooltip }: FeedbackButtonProps) {
  const [closedManually, setClosedManually] = useState(false);
  const [forceFeedbackTooltip, setForceFeedbackTooltip] = useState<
    boolean | undefined
  >(undefined);

  const { t } = useTranslation('wmgone', {
    keyPrefix: 'globalHeader',
  });

  const feedbackUrl = env('VITE_WMG_FEEDBACK_URL');

  useEffect(() => {
    const now = Date.now();

    const showTooltip = () => {
      setForceFeedbackTooltip(true);
      localStorage.setItem(
        FEEDBACK_TOOLTIP_STORAGE_KEY,
        JSON.stringify({ lastOpened: now }),
      );
    };

    const lastOpened = getLastOpened(
      localStorage.getItem(FEEDBACK_TOOLTIP_STORAGE_KEY),
    );

    if (!lastOpened || now - lastOpened > SEVEN_DAYS) {
      showTooltip();
    }
  }, []);

  const onHideTooltip = () => {
    setForceFeedbackTooltip(false);
    setClosedManually(true);
  };

  return feedbackUrl ? (
    <Tooltip
      size="deprecated-help"
      spacing="thin"
      onOpenChange={(open) => {
        if (closedManually) {
          setForceFeedbackTooltip(open);
        }
      }}
      content={
        <>
          <div className="relative mb-sm leading-tight">
            <div className="absolute right-0 top-0">
              <IconButton
                iconType="xmark"
                onClick={onHideTooltip}
                aria-label="Close"
                size="small"
              />
            </div>
            <div className="mb-md basis-full text-body-md font-semibold">
              {t('feedback.tooltip.heading')}
            </div>
            <div className="flex gap-md">
              <img
                src="/images/feedback-avatar-geo.jpg"
                alt={t('feedback.tooltip.avatar')}
                className="aspect-square size-xl basis-xl rounded-full object-cover outline outline-2 transition-transform duration-300 ease-out bg-emphasis-subtle outline-emphasis-subtle hover:scale-150"
              />
              <p className="text-body-sm">
                {t('feedback.tooltip.description')}
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="primary" asChild>
              <a
                href={feedbackUrl}
                target="_blank"
                rel="noreferrer noopener"
                onClick={onHideTooltip}
              >
                {t('feedback.tooltip.link')}
              </a>
            </Button>
          </div>
        </>
      }
      triggerAsChild
      sideOffset={8}
      alignOffset={-8}
      delayDuration={0}
      align="end"
      open={!hideTooltip && forceFeedbackTooltip}
    >
      <div className="flex items-stretch">
        <Button variant="secondary" asChild>
          <a
            href={feedbackUrl}
            className="grid place-items-center"
            target="_blank"
            rel="noreferrer noopener"
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            onClick={onHideTooltip}
          >
            {t('feedback.button')}
          </a>
        </Button>
      </div>
    </Tooltip>
  ) : null;
}

FeedbackButton.displayName = 'FeedbackButton';
